import React, { createContext, useState, ReactNode } from 'react';

// Define the type for error messages
type ErrorMessages = {
    [key: string]: string;
};

// Define your error messages mapping
const errorMessages: ErrorMessages = {
    "ERROR_OCCURED": 'errors.errorOccurred',
    "INVALID_INPUT": 'errors.invalidInput',
    "EMAIL_EXISTS": 'errors.emailExists',
    "EMAIL_NOT_FOUND": 'errors.emailNotFound',
    "EMAIL_NOT_VERIFIED": 'errors.emailNotVerified',
    "WRONG_PASSWORD": 'errors.wrongPassword',
    "CUR_PASS_INCORRECT": 'errors.curPassIncorrect',
    "WRONG_IMAGE_FILE_TYPE": 'errors.wrongImageFileType',
    "IMAGE_FILE_TOO_LARGE": 'errors.imageFileTooLarge',
    "FILE_TOO_LARGE": 'errors.fileTooLarge',
    "NO_PERMISSION": 'errors.noPermission',
    "TOO_MANY_REQUESTS": 'errors.tooManyRequests',
    "TOO_MANY_LOGIN_ATTEMPTS": 'errors.tooManyLoginAttempts',
    "ERROR_UPLOADING_FILE": 'errors.errorUploadingFile',
    "TOO_MANY_UPLOADS": 'errors.tooManyUploads',
    "NO_TITLE": "taskErrors.taskTitle",
    "SKILL_CATEGORY": "taskErrors.skillCategory"
};

// Define the error context type
interface ErrorContextType {
    globalError: string | null;
    setGlobalError: (message: string | null) => void;
    handleGlobalError: (error: any, t: (key: string) => string) => void;
    handleLocalError: (error: any, t: (key: string) => string) => string;
    showPopupError: (error: any, t: (key: string) => string) => void;
}

// Create the context
const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [popupError, setPopupError] = useState<string | null>(null);


    // Error handler function that uses the errorMessages mapping
    const handleGlobalError = (error: any, t: (key: string) => string) => {
        const errorCode = error.response?.data.message;
        const errorMessage = errorMessages[errorCode] ? t(errorMessages[errorCode]) : t('errors.errorOccurred');
        setGlobalError(errorMessage);
    };

    const handleLocalError = (error: any, t: (key: string) => string) => {
        const errorCode = error.response?.data?.message || error.message;
        const errorMessage = errorMessages[errorCode] ? t(errorMessages[errorCode]) : t('errors.errorOccurred');
        return errorMessage;
    };

    const showPopupError = (error: any, t: (key: string) => string) => {
        const errorCode = error.response?.data?.message;
        const errorMessage = errorMessages[errorCode] ? t(errorMessages[errorCode]) : t('errors.errorOccurred');
        setPopupError(errorMessage);

        setTimeout(() => setPopupError(null), 3000);
    };

    return (
        <ErrorContext.Provider value={{ globalError, setGlobalError, handleGlobalError, handleLocalError, showPopupError }}>
            {children}
            {popupError && (
                <div className="popup-error">
                    {popupError}
                </div>
            )}
        </ErrorContext.Provider>
    );
};

// // Custom hook to use error context
export const useError = (): ErrorContextType => {
    const context = React.useContext(ErrorContext);
    if (!context) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};